import React, { useContext } from 'react';
import { useInformeBySlug } from '../../../../hooks/useWordPressData';
import { LangContext } from '../../../../context/DataContext';
import { reportData } from '../../../../data/report-data/report-data/reportData';
import slugify from 'react-slugify';
import useCurrentSlug from '../../../../hooks/useCurrentSlug';

// Función para generar un slug limpio para las anclas
const generateAnchorSlug = (text) => {
    return text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '') // Remover caracteres especiales
        .replace(/\s+/g, '-') // Reemplazar espacios con guiones
        .replace(/-+/g, '-') // Reemplazar múltiples guiones con uno solo
        .trim();
};

const WordPressSidebar = ({ slug }) => {
    const { myLang } = useContext(LangContext);
    const currentSlug = useCurrentSlug();
    
    // Usar el slug pasado como prop o detectar automáticamente desde la URL
    const slugToUse = slug || currentSlug;
    
    const { informe, loading, error } = useInformeBySlug(slugToUse, true, myLang);
    
    // Función para obtener el contenido del sidebar desde reportData basándose en el slug actual
    const getSidebarContentFromReportData = (currentSlug) => {
        // Buscar en reportData si algún reporte tiene títulos que generen el slug actual
        for (const [, reportContent] of Object.entries(reportData)) {
            // Generar slugs para cada idioma y ver si alguno coincide con el actual
            const generatedSlugs = {
                es: slugify(reportContent.es?.title || ''),
                en: slugify(reportContent.en?.title || ''),
                pt: slugify(reportContent.pt?.title || '')
            };

            // Si el slug actual coincide con alguno de los generados, devolver el contenido
            if (generatedSlugs.es === currentSlug || 
                generatedSlugs.en === currentSlug || 
                generatedSlugs.pt === currentSlug) {
                
                return {
                    title: reportContent[myLang]?.title || reportContent.es?.title || '',
                    items: reportContent[myLang]?.items || reportContent.es?.items || []
                };
            }
        }
        return null;
    };
    
    // Obtener contenido del sidebar
    const sidebarContent = getSidebarContentFromReportData(slugToUse);
    
    // Debug info (solo en desarrollo)
    if (process.env.NODE_ENV === 'development') {
        console.log('📋 WordPressSidebar DEBUG:', {
            slug,
            currentSlug,
            slugToUse,
            myLang,
            sidebarContent,
            informe: informe ? {
                title: informe.title,
                secciones: informe.secciones?.length || 0
            } : null,
            hasWordPressContent: !!(informe && informe.secciones),
            hasReportDataContent: !!sidebarContent
        });
    }

    // Determinar qué contenido usar (WordPress o reportData como fallback)
    const title = informe?.title || sidebarContent?.title || 'Cargando...';
    const sections = informe?.secciones || [];
    const items = sidebarContent?.items || [];

    if (loading) {
        return (
            <div className="sidebar-loading">
                <></>
            </div>
        );
    }

    // Si no hay contenido ni de WordPress ni de reportData
    if ((error || !informe || !informe.secciones) && !sidebarContent) {
        return (
            <div className="sidebar-error">
                <p>No se pudo cargar el índice</p>
            </div>
        );
    }

    // Función para renderizar items del sidebar (tanto de WordPress como de reportData)
    const renderSidebarItems = () => {
        // Si tenemos secciones de WordPress, usar esas
        if (sections.length > 0) {
            return sections.map((seccion, index) => {
                const sectionAnchor = generateAnchorSlug(seccion.titulo);
                
                return (
                    <li key={index} className="seccion-item">
                        <a
                            href={`#${sectionAnchor}`}
                            className="seccion-link"
                        >
                            {seccion.titulo}
                        </a>
                        
                        {/* Renderizar subsecciones si existen */}
                        {seccion.subsecciones && seccion.subsecciones.length > 0 && (
                            <ul className="active">
                                {seccion.subsecciones.map((subseccion, subIndex) => {
                                    const subSectionAnchor = generateAnchorSlug(subseccion.titulo_de_la_subseccion);
                                    
                                    return (
                                        <li key={subIndex} className="subseccion-item">
                                            <a
                                                href={`#${subSectionAnchor}`}
                                                className="subseccion-link"
                                            >
                                                {subseccion.titulo_de_la_subseccion}
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </li>
                );
            });
        }
        
        // Si no hay secciones de WordPress, usar items de reportData
        if (items.length > 0) {
            return items.map((item, index) => {
                const sectionAnchor = generateAnchorSlug(item.label);
                
                return (
                    <li key={index} className="seccion-item">
                        <a
                            href={`#${sectionAnchor}`}
                            className="seccion-link"
                        >
                            {item.label}
                        </a>
                        
                        {/* Renderizar subitems si existen */}
                        {item.subitems && item.subitems.length > 0 && (
                            <ul className="active">
                                {item.subitems.map((subitem, subIndex) => {
                                    const subSectionAnchor = generateAnchorSlug(subitem.label);
                                    
                                    return (
                                        <li key={subIndex} className="subseccion-item">
                                            <a
                                                href={`#${subSectionAnchor}`}
                                                className="subseccion-link"
                                            >
                                                {subitem.label}
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </li>
                );
            });
        }
        
        return null;
    };

    return (
        <nav className="wordpress-sidebar">
            <div>  
                <button className="report-title-button">
                    {title}
                </button>
                <ul className="active">
                    {renderSidebarItems()}
                </ul>
            </div>
        </nav>
    );
};

export default WordPressSidebar;
